import ENDPOINTS from 'shared/api/endpoints';
import RequestClient from 'shared/api/http-client';

import type { SystemBetRow, TicketSlipResolvedType, TicketTip } from './types';

export type CreateTicketParams = {
  ticket_slip: TicketTip[];
  total_stake: number;
  player_id: string | number;
  bet_type: TicketSlipResolvedType;
  system_rows?: SystemBetRow[];
};

export type CreatedTicket = {
  ticket_id?: string | number;
  new_player_balance?: number | string;
  [k: string]: unknown;
};

export async function createTicket(params: CreateTicketParams): Promise<CreatedTicket> {
  const { ticket_slip, total_stake, player_id, bet_type, system_rows } = params;

  // Backend fetches odds from outcomes; send only identifiers per tip.
  const slipForApi = ticket_slip.map((t) => {
    const tip: {
      event_id: string | number;
      outcome_id: string | number;
      market_type_id?: string | number;
      stake?: number;
    } = {
      event_id: t.event_id,
      outcome_id: t.outcome_id,
    };
    if (t.market_type_id != null) tip.market_type_id = t.market_type_id;
    if (bet_type === 'DUTCHING' && t.stake != null) {
      tip.stake = Number(t.stake);
    }
    return tip;
  });

  const payload =
    bet_type === 'SYSTEM'
      ? {
          ticket: JSON.stringify(slipForApi),
          bet_type,
          system_rows: (system_rows ?? []).map((row) => ({
            size: row.size,
            stake_per_line: row.stake_per_line,
          })),
          player_id: String(player_id),
        }
      : {
          ticket: JSON.stringify(slipForApi),
          bet_type,
          total_stake,
          player_id: String(player_id),
        };

  const res = await RequestClient.post(ENDPOINTS.TICKETS_CREATE, payload);

  return res.data as CreatedTicket;
}

export type RichTicketTip = {
  tip_id: number;
  ticket_id?: number;
  event_id: number;
  outcome_id: number;
  market_type_id: number;
  stake: number;
  odd: number;
  win: number;
  status: string;
  settled: boolean;
  event_description: string | null;
  event_datetime: string | null;
  event_status: string | null;
  sport_id: number | null;
  outcome_label: string | null;
  outcome_description: string | null;
  market_type_description: string | null;
  [k: string]: unknown;
};

export type RichTicketSystemRow = {
  size: number;
  label: string;
  stake_per_line: number;
  combination_count: number;
  total_stake: number;
  minimum_potential_payout: number;
  maximum_potential_payout: number;
  total_potential_payout: number;
};

export type RichTicketCombination = {
  ticket_combination_id: number;
  ticket_id: number;
  row_size: number;
  sequence_index: number;
  stake: number;
  combined_odd: number;
  potential_payout: number;
  win: number;
  status: string;
  settled: boolean;
  legs: RichTicketTip[];
  [k: string]: unknown;
};

export type RichTicket = {
  ticket_id: number;
  ticket_timestamp: number;
  ticket_datetime: string;
  type: string;
  num_tips: number;
  bets: number;
  wins: number;
  minimum_potential_payout: number;
  maximum_potential_payout: number;
  status: string;
  settled: boolean;
  tips: RichTicketTip[];
  system_rows?: RichTicketSystemRow[];
  combinations?: RichTicketCombination[];
  [k: string]: unknown;
};

export async function fetchTicketsRich(): Promise<RichTicket[]> {
  const res = await RequestClient.get(ENDPOINTS.TICKETS_LIST);
  const data = res.data;
  if (data === undefined) throw new Error('API response missing data');
  return (Array.isArray(data) ? data : []) as RichTicket[];
}

export async function fetchTicketsForEvent(eventId: number | string): Promise<RichTicket[]> {
  const res = await RequestClient.get(ENDPOINTS.TICKETS_LIST, {
    params: { event_id: eventId },
  });
  const data = res.data;
  if (data === undefined) throw new Error('API response missing data');
  return (Array.isArray(data) ? data : []) as RichTicket[];
}

export type CreditAvailableResponse = {
  credit_available?: number | string;
  [k: string]: unknown;
};

export async function fetchCreditAvailable(): Promise<CreditAvailableResponse> {
  const res = await RequestClient.get(ENDPOINTS.REQUEST_CREDIT);
  return res.data as CreditAvailableResponse;
}

export type RebuyParams = {
  amount: number | string;
};

export type RebuyResponse = {
  rebuy_amount?: number | string;
  [k: string]: unknown;
};

export async function requestRebuy(params: RebuyParams): Promise<RebuyResponse> {
  const res = await RequestClient.get(ENDPOINTS.REQUEST_REBUY, {
    params: { amount: params.amount },
  });
  return res.data as RebuyResponse;
}

